import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImageExtension from 'tiptap-extension-resize-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Footer from '../Footer';
import './Me.css';
import '../pages/BlogEditor.css';

const DEFAULTS = {
  name: 'About Me',
  tagline: 'Builder of things. Thinker of thoughts.',
  sections: [
    {
      id: 'who',
      icon: 'fas fa-id-card',
      heading: 'Who Am I?',
      type: 'paragraph',
      content: "Welcome to my corner of the internet. This is a space to share what I know, what I'm learning, and the tools I find useful along the way."
    },
    {
      id: 'what',
      icon: 'fas fa-briefcase',
      heading: 'What I Do',
      type: 'paragraph',
      content: 'I work at the intersection of technology and problem-solving — with a focus on Azure, Microsoft 365, AI, and modern IT infrastructure.'
    },
    {
      id: 'interests',
      icon: 'fas fa-star',
      heading: 'Interests',
      type: 'list',
      items: [
        { icon: 'fas fa-cloud',      text: 'Azure & Cloud Architecture' },
        { icon: 'fas fa-brain',      text: 'AI & Automation' },
        { icon: 'fab fa-microsoft',  text: 'Microsoft 365' },
        { icon: 'fas fa-shield-alt', text: 'Security & Compliance' },
        { icon: 'fas fa-code',       text: 'Building useful things' },
        { icon: 'fas fa-cubes',      text: 'Kubernetes / Containerisation & Microservices' },
        { icon: 'fas fa-file-code',  text: 'Infrastructure as Code' },
        { icon: 'fas fa-map',        text: 'Technical Strategy & Roadmapping' }
      ]
    },
    {
      id: 'contact',
      icon: 'fas fa-envelope',
      heading: 'Get In Touch',
      type: 'paragraph',
      content: 'Have a question, suggestion, or just want to say hi? Feel free to reach out.'
    }
  ]
};

// ── Rich text editor for a single Me section ───────────────────────────────
function MeSectionEditor({ initialContent, onChange, getAuthToken }) {
  const [imageUploading, setImageUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImageExtension,
      Link.configure({ openOnClick: false }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: initialContent || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: { attributes: { class: 'tiptap-editor me-section-tiptap' } },
  });

  const addImage = useCallback(async (file) => {
    if (!editor) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = await getAuthToken();
      const res = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        editor.chain().focus().setImage({ src: data.url }).run();
      }
    } catch (e) { /* no-op */ }
    setImageUploading(false);
  }, [editor, getAuthToken]);

  return (
    <div className='tiptap-wrapper me-section-editor'>
      {editor && (
        <div className='tiptap-toolbar'>
          <button onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}><strong>B</strong></button>
          <button onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}><em>I</em></button>
          <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''}><u>U</u></button>
          <button onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}><s>S</s></button>
          <span className='separator' />
          <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}>H2</button>
          <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}>H3</button>
          <span className='separator' />
          <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}>• List</button>
          <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''}>1. List</button>
          <span className='separator' />
          <button onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''} title='Align Left'><i className='fas fa-align-left' /></button>
          <button onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''} title='Align Center'><i className='fas fa-align-center' /></button>
          <button onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''} title='Align Right'><i className='fas fa-align-right' /></button>
          <span className='separator' />
          <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'is-active' : ''}>&quot; Quote</button>
          <span className='separator' />
          <button
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = async (e) => { const f = e.target.files[0]; if (f) await addImage(f); };
              input.click();
            }}
          >🖼️ Image</button>
          {imageUploading && <span style={{ color: '#4BC4B9', fontSize: '0.85rem', marginLeft: 8 }}><i className='fas fa-spinner fa-spin' /> Uploading...</span>}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

function Me() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [avatarExists, setAvatarExists] = useState(false);
  const [avatarTs, setAvatarTs] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [content, setContent] = useState(DEFAULTS);
  const [draft, setDraft] = useState(null);
  const fileInputRef = useRef(null);

  // Load content and avatar on mount
  useEffect(() => {
    async function loadData() {
      try {
        const res = await fetch('/api/me-content');
        if (res.ok) {
          const data = await res.json();
          if (data) setContent(data);
        }
      } catch (e) { /* use defaults */ }

      try {
        const res = await fetch('/api/me-avatar', { method: 'HEAD' });
        if (res.ok) {
          setAvatarExists(true);
          setAvatarTs(Date.now());
        }
      } catch (e) { /* no avatar */ }
    }
    loadData();
  }, []);

  const getAuthToken = async () => {
    if (!accounts[0]) return null;
    const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return result.idToken;
  };

  // ── Avatar ────────────────────────────────────────────────────────────────
  const handleAvatarClick = () => { if (isAuthenticated) fileInputRef.current.click(); };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/me-avatar', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        setAvatarExists(true);
        setAvatarTs(Date.now());
      }
    } catch (e) { /* no-op */ }
  };

  const handleRemoveAvatar = async (e) => {
    e.stopPropagation();
    try {
      const token = await getAuthToken();
      await fetch('/api/me-avatar', {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (e) { /* no-op */ }
    setAvatarExists(false);
    setAvatarTs(null);
  };

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const startEditing = () => {
    setDraft(JSON.parse(JSON.stringify(content)));
    setEditMode(true);
  };

  const saveEditing = async () => {
    try {
      const token = await getAuthToken();
      await fetch('/api/me-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(draft),
      });
    } catch (e) { /* no-op */ }
    setContent(draft);
    setEditMode(false);
    setDraft(null);
  };

  const cancelEditing = () => {
    setEditMode(false);
    setDraft(null);
  };

  const setField = (field, value) =>
    setDraft(prev => ({ ...prev, [field]: value }));

  const setSection = (id, field, value) =>
    setDraft(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === id ? { ...s, [field]: value } : s)
    }));

  const setListItem = (sectionId, idx, value) =>
    setDraft(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        const items = [...s.items];
        items[idx] = { ...items[idx], text: value };
        return { ...s, items };
      })
    }));

  const removeSection = (id) =>
    setDraft(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== id)
    }));

  const data = editMode ? draft : content;

  return (
    <>
      <div className='me-page'>

        {/* ── Edit controls ─────────────────────────────────────────────── */}
        <div className='me-edit-controls'>
          {isAuthenticated && (
            editMode ? (
              <>
                <button className='me-edit-btn me-save-btn' onClick={saveEditing}>
                  <i className='fas fa-check'></i> Save
                </button>
                <button className='me-edit-btn me-cancel-btn' onClick={cancelEditing}>
                  <i className='fas fa-times'></i> Cancel
                </button>
              </>
            ) : (
              <button className='me-edit-btn' onClick={startEditing}>
                <i className='fas fa-pencil-alt'></i> Edit Page
              </button>
            )
          )}
        </div>

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <div className='me-hero'>
          <div className='me-avatar' onClick={handleAvatarClick} title={isAuthenticated ? 'Click to upload a photo' : ''} style={{ cursor: isAuthenticated ? 'pointer' : 'default' }}>
            {avatarExists
              ? <img src={`/api/me-avatar?t=${avatarTs}`} alt='Profile' className='me-avatar-img' />
              : <i className='fas fa-user-circle'></i>
            }
            {isAuthenticated && <div className='me-avatar-overlay'><i className='fas fa-camera'></i></div>}
            {isAuthenticated && avatarExists && (
              <button className='me-avatar-remove' onClick={handleRemoveAvatar} title='Remove photo'>
                <i className='fas fa-times'></i>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type='file' accept='image/*' style={{ display: 'none' }} onChange={handleFileChange} />

          {editMode
            ? <input className='me-edit-input me-name-input' value={draft.name} onChange={e => setField('name', e.target.value)} />
            : <h1 className='me-name'>{data.name}</h1>
          }
          {editMode
            ? <input className='me-edit-input me-tagline-input' value={draft.tagline} onChange={e => setField('tagline', e.target.value)} />
            : <p className='me-tagline'>{data.tagline}</p>
          }
        </div>

        {/* ── Sections ──────────────────────────────────────────────────── */}
        <div className='me-content'>
          {data.sections.map(section => {
            const draftSection = editMode ? draft.sections.find(s => s.id === section.id) : null;
            return (
              <section key={section.id} className='me-section'>
                <h2>
                  <i className={section.icon}></i>
                  {editMode
                    ? <input className='me-edit-input me-heading-input' value={draftSection.heading} onChange={e => setSection(section.id, 'heading', e.target.value)} />
                    : <span>{section.heading}</span>
                  }
                  {editMode && (
                    <button
                      className='me-section-remove-btn'
                      onClick={() => removeSection(section.id)}
                      title='Remove section'
                    >
                      <i className='fas fa-trash-alt'></i>
                    </button>
                  )}
                </h2>

                {section.type === 'paragraph' && (
                  editMode
                    ? <MeSectionEditor
                        key={section.id}
                        initialContent={draftSection.content}
                        onChange={val => setSection(section.id, 'content', val)}
                        getAuthToken={getAuthToken}
                      />
                    : <div className='me-rich-content' dangerouslySetInnerHTML={{ __html: section.content }} />
                )}

                {section.type === 'list' && (
                  <ul className='me-list'>
                    {section.items.map((item, idx) => (
                      <li key={idx}>
                        <i className={item.icon}></i>
                        {editMode
                          ? <input className='me-edit-input me-list-input' value={draftSection.items[idx].text} onChange={e => setListItem(section.id, idx, e.target.value)} />
                          : <span>{item.text}</span>
                        }
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Me;

